import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import IntakeWizard from '@/components/IntakeWizard';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { useAuth } from '@/context/AuthContext';
import { Loader2, Sparkles, Workflow } from 'lucide-react';
import { toast } from 'sonner';

export default function Intake() {
  const navigate = useNavigate();
  const { clientId: paramId } = useParams();
  const { activeClient, activeId, refresh } = useClient();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const targetId = paramId || activeId;

  useEffect(() => {
    if (!targetId) return;
    api.get(`/clients/${targetId}`).then(r => setClient(r.data));
  }, [targetId]);

  if (!targetId) {
    return <div className="text-sm text-muted-foreground">No client selected.</div>;
  }
  if (!client) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const isClientRole = user?.role === 'client';

  const onSubmit = async (payload) => {
    setSubmitting(true);
    try {
      await api.post(`/clients/${client.client_id}/intake`, payload);
      toast.success('Intake submitted. The AEO process is starting now — your assessment will be ready in about a minute.');
      await refresh();
      navigate('/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit intake');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <IntakeWizard
      initialClient={client}
      submitLabel="Submit & Begin AEO Process"
      submitting={submitting}
      onSubmit={onSubmit}
      intro={
        <div className="mb-8 space-y-6">
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-6">
            <div className="flex items-center gap-2 text-primary">
              <Sparkles className="h-5 w-5" />
              <div className="text-xs uppercase tracking-widest font-medium">
                {isClientRole ? 'Welcome to your client portal' : 'Begin the AEO process'}
              </div>
            </div>
            <h1 className="mt-3 font-display text-3xl lg:text-4xl tracking-tight">
              Tell us about {client.company_name}.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-xl">
              The more accurately you complete this brief, the sharper the Authority Engineering (AEO) assessment will be.
              When you submit, the agentic swarm begins its analysis automatically — your full assessment, scores, 30/90/365-day plan and competitor landscape will be ready within minutes.
            </p>
            <div className="mt-5 grid sm:grid-cols-3 gap-3 text-xs">
              <Step n={1} title="Provide your brief" active />
              <Step n={2} title="AEO + AI Readiness run" />
              <Step n={3} title="Authority Score™ + Plan" />
            </div>
          </div>
        </div>
      }
    />
  );
}

function Step({ n, title, active }) {
  return (
    <div className={`rounded-md border p-3 ${active ? 'border-primary bg-card' : 'border-border bg-secondary/30'}`}>
      <div className="flex items-center gap-2">
        <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-medium ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
          {n}
        </div>
        <div className={`text-xs font-medium ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{title}</div>
      </div>
    </div>
  );
}
